//-- Copyright (c) 2021 TMT Insights Group, LLC
import { AllTableColumnsConfig } from '@/__configs/config_types';
import { getAgeFromCreatedAndCompleted } from '@tmt-insights/oms_fe_core/date';
import { Tooltip, Typography } from '@tmt-insights/oms_fe_core/material';
import { getLineageMetaTitle } from '@tmt-insights/oms_fe_core/utils';
import {
  AssigneeSelect,
  DateTimeDisplay,
  History,
  OverflowMenu,
  PolarisIcon,
  PriorityNumber,
  TableStatus,
  TaskAction,
  Notes,
  TaskType
} from '@tmt-insights/oms_fe_core/columns';
import { LineageMeta, OrderJobTask, UserProfile } from '@/redux/slices/slice_types';

const columns: AllTableColumnsConfig<OrderJobTask> = {
  priority: {
    label: (
      <Tooltip title="Priority">
        <PolarisIcon icon="priority" height={20} style={{ verticalAlign: 'middle' }} />
      </Tooltip>
    ),

    minWidth: 50,
    render: (cell: OrderJobTask['priority']) => {
      return (
        <Tooltip title="Sort by priority and filter by assignee to assign priority">
          <div style={{ position: 'relative' }}>
            {/* negative margin and padding to force tooltip hover area to be larger */}
            {Number(cell) ? (
              <PriorityNumber priority={cell} />
            ) : (
              <div style={{ display: 'block', margin: -20, padding: 20 }}>
                <PolarisIcon icon="empty" height={10} style={{ marginLeft: 0 }} />
              </div>
            )}
          </div>
        </Tooltip>
      );
    },
    draggable: (_cell: any, row: OrderJobTask) => row.status !== 'complete' && row.status !== 'cancelled' && !!row.assigned_user
  },
  hid: {
    label: 'Task ID',
    sortField: 'id',
    render: (cell: OrderJobTask['hid']) => <strong>{cell}</strong>
  },
  step: {
    label: 'Task Type',
    sortField: 'step',
    minWidth: 150,
    render: (cell: OrderJobTask['step'], row: OrderJobTask) => <TaskType type={cell} prettyName={row.pretty_name} />
  },
  job__due_date: {
    label: 'Due Date',
    minWidth: 100,
    render: (cell: OrderJobTask['job']['due_date']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  created_at_dateformat: {
    sortField: 'created_at',
    label: 'Created',
    minWidth: 120,
    render: (_cell: OrderJobTask['created_at'], row: OrderJobTask) => <DateTimeDisplay>{row.created_at}</DateTimeDisplay>
  },
  updated_at: {
    sortField: 'updated_at',
    label: 'Last Updated',
    minWidth: 120,
    render: (_cell: OrderJobTask['updated_at'], row: OrderJobTask) => <DateTimeDisplay>{row.updated_at}</DateTimeDisplay>
  },
  created_at: {
    label: 'Age',
    minWidth: 120,
    render: (cell: OrderJobTask['created_at'], row: OrderJobTask) => (
      <div style={{ whiteSpace: 'nowrap' }}>{getAgeFromCreatedAndCompleted(row.created_at, row.completed_at)}</div>
    )
  },
  job__title__lineage_meta__series_name: {
    label: 'Title',
    render: (cell: LineageMeta['series_name'], row: OrderJobTask) =>
      !cell ? (
        row.job?.title?.name
      ) : (
        <>
          <Typography sx={{ minWidth: 250 }}>
            <strong>{cell}</strong>
          </Typography>
          <Typography variant="caption">{getLineageMetaTitle(row.job.title)}</Typography>
          {row.job.version?.name && (
            <>
              <br />
              <Typography variant="caption" sx={{ color: '#667799' }}>
                {row.job.version?.name}
              </Typography>
            </>
          )}
        </>
      )
  },
  job__output_network: {
    sortField: 'job__output_version__meta__network_name',
    label: 'Network',
    render: (cell: OrderJobTask['job']['output_network']) => {
      const networkName = cell && Object.keys(cell).length > 0 ? cell[Object.keys(cell)[0]] : null;
      return <Typography>{networkName && networkName.length > 0 ? networkName : '-'}</Typography>;
    }
  },
  job__version__rally_asset_name: {
    label: 'Rally Asset',
    minWidth: 200,
    defaultDisabled: true,
    render: (cell: string) => cell ?? '-'
  },
  job__version__meta__broadcast_length: {
    label: 'Broadcast Length',
    render: (cell: number) => cell ?? '-'
  },
  status: {
    label: 'Status',
    minWidth: 120,
    render: (cell: OrderJobTask['status'], row: OrderJobTask) => (
      // only display status reason if status is failed or in_error
      <TableStatus status={cell} statusReason={row.status === 'failed' || row.status === 'in_error' ? row.status_reason : undefined} />
    )
  },
  assigned_user: {
    label: 'Assignee',
    minWidth: 135,
    render: (cell: OrderJobTask['assigned_user'], row: OrderJobTask) => (
      <AssigneeSelect value={cell as UserProfile} id={row.id} assigns="user" priority={row.priority} />
    ),
    disableSort: true
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: OrderJobTask) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} />
      </div>
    ),
    disableSort: true
  },
  action: {
    label: 'Action',
    disableSort: true,
    minWidth: 75,
    render: (_cell: any, row: OrderJobTask) => <TaskAction task={row} />
  },
  overflow: {
    label: '',
    isOverflow: true,
    render: (_cell: any, row: OrderJobTask) => (
      <OverflowMenu>
        <History data={row} />
      </OverflowMenu>
    )
  }
};

export default columns;
